import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// Add this import at the top
import { FiDatabase } from 'react-icons/fi'; // icon for backup
import {
  FiHome,
  FiUsers,
  FiGlobe,
  FiSettings,
  FiChevronDown,
  FiChevronRight,
  FiMenu,
  FiX,
  FiDollarSign,
  FiPackage,
  FiShoppingCart,
  FiTrendingUp,
  FiLayers,
  FiBriefcase,
  FiFileText,
  FiUserPlus,
  FiUserCheck,
  FiCreditCard ,
  FiList,
  FiPlusCircle,
  FiRepeat,
  FiBookOpen,
  FiFilePlus
} from 'react-icons/fi';
import './Sidebar.css';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  // System Admin/Developer Menu (unchanged)
  const systemMenuItems = [
    { name: 'Dashboard', icon: <FiHome />, path: '/', submenu: [] },
    { name: 'Institution', icon: <FiGlobe />, path: '/ViewInstitution', submenu: [] },
    { name: 'Company', icon: <FiBriefcase />, path: '/ViewCompany', submenu: [] },
    {
      name: 'Settings',
      icon: <FiSettings />,
      path: null,
      submenu: [
        { name: 'Currency', path: '/ViewCurrency', icon: <FiDollarSign /> },
        { name: 'Country', path: '/ViewCountry', icon: <FiGlobe /> },
        { name: 'BusinessType', path: '/ViewBusinessType', icon: <FiGlobe /> },
        { name: 'Role', path: '/ViewRole', icon: <FiUsers /> },
      ]
    },
    { name: 'Users', icon: <FiUsers />, path: '/settings', submenu: [] },
    { name: 'AuditLogs', icon: <FiUsers />, path: '/ViewAuditLogs', submenu: [] },
    { name: 'Subscriptions', icon: <FiCreditCard  />, path: '/Subscriptions', submenu: [] },
    {
      name: 'Backup Database',
      icon: <FiDatabase />,
      path: null,
      action: async () => {
        try {
          // Update the URL to match your Node server port
          const response = await fetch('http://localhost:4000/api/backup', { method: 'POST' });
          const data = await response.json();
          if (data.success) alert('Backup completed: ' + data.folder);
          else alert('Backup failed');
        } catch (error) {        
          alert('Backup failed');
        }
      }
    }
  ];

  // Company Admin Menu (unchanged)
  const companyAdminMenuItems = [
    { name: 'Dashboard', icon: <FiHome />, path: '/', submenu: [] },
    { name: 'Company', icon: <FiBriefcase />, path: '/ViewCompany', submenu: [] },
    {
      name: 'Users',
      icon: <FiUsers />,
      path: null,
      submenu: [
        { name: 'User List', path: `/company/${company?._id}/users`, icon: <FiUserCheck /> },
        { name: 'Add User', path: `/company/${company?._id}/users/add`, icon: <FiUserPlus /> },
      ]
    },
    {
      name: 'Inventory',
      icon: <FiPackage />,
      path: null,
      submenu: [
        { name: 'Products', path: '/ViewProducts', icon: <FiPackage /> },
        { name: 'Opening Balance', path: '/ViewOpeningBalance', icon: <FiShoppingCart /> },
        { name: 'Stock', path: '/ViewInventoryStock', icon: <FiTrendingUp /> },
        { name: 'Stock Adjustments', path: '/ViewStockAdjustments', icon: <FiTrendingUp /> },
      ]
    },
    {
      name: 'Purchase',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Inventory Transaction', path: '/ViewPurchaseInventoryTransaction', icon: <FiShoppingCart /> },   
        { name: 'Suppliers', path: '/ViewSuppliers', icon: <FiUsers /> },    
        { name: 'Purchase Order', path: '/ViewPurchaseOrder', icon: <FiShoppingCart /> },
        { name: 'Purchase', path: '/purchase/invoices', icon: <FiFileText /> },
        { name: 'Suppliers Payment', path: '/ViewSupplierPayment', icon: <FiUsers /> },    
      ]
    },
    {
      name: 'Sales',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Inventory Transaction', path: '/ViewSalesInventoryTransaction', icon: <FiShoppingCart /> },       
        { name: 'Customer', path: '/ViewCustomer', icon: <FiShoppingCart /> },
        { name: 'Sales Order', path: '/ViewSalesOrder', icon: <FiShoppingCart /> },
        { name: 'Sales', path: '/sales/invoices', icon: <FiFileText /> },
        { name: 'Customer Payment', path: '/ViewCustomerPayment', icon: <FiUsers /> },    
      ]
    },
    {
      name: 'Transfer',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Stock Transfer', path: '/ViewStockTransfer', icon: <FiShoppingCart /> },       
      ]
    },
    // {
    //   name: 'Finance',
    //   icon: <FiShoppingCart/>,
    //   path: null,
    //   submenu: [
    //     { name: 'Payment Type', path: '/ViewPaymenttype', icon: <FiShoppingCart /> }, 
    //     { name: 'Account Form', path: '/ViewAccounts', icon: <FiShoppingCart /> },       
    //     { name: 'Account Transfer', path: '/ViewTransfers', icon: <FiShoppingCart /> },       
    //   ]
    // },
    {
      name: "Finance",
      icon: <FiDollarSign />,
      path: null,
      submenu: [
        {
          name: "Payment Methods",
          path: "/ViewPaymentType",
          icon: <FiCreditCard />,
        },
        {
          name: "Accounting Type List",
          path: "/ViewAccountingTypes",
          icon: <FiList />,
        },
        {
          name: "Add Accounting Type",
          path: "/AddAccountingType",
          icon: <FiPlusCircle />,
        },
        {
          name: "Chart of Accounts",
          path: "/ViewAccounts",
          icon: <FiBookOpen />,
        },
        {
          name: "Beginning Balances",
          path: "/BeginningBalances",
          icon: <FiFileText />,
        },
        {
          name: "Account Transfer",
          path: "/ViewTransfers",
          icon: <FiRepeat />,
        },
        // ✅ NEW ENTRY
        {
          name: "Manual Journal Entry",
          path: "/manual-journal/new",
          icon: <FiFilePlus />,
        },
      ],
    },
    {
      name: 'Expenses',
      icon: <FiShoppingCart/>,
      path: null,
      submenu: [
        { name: 'Expense Form', path: '/ViewExpenses', icon: <FiShoppingCart /> },       
      ]
    },
    {
      name: 'MySubscription',
      icon: <FiCreditCard/>,
      path: null,
      submenu: [
        { name: 'MySubscription', path: '/MySubscription', icon: <FiShoppingCart /> },       
      ]
    },
    {
      name: 'Settings',
      icon: <FiSettings />,
      path: null,
      submenu: [
        { name: 'Transaction Type', path: '/ViewTransactionType', icon: <FiLayers /> },
        { name: 'Warehouses', path: '/ViewWareHouses', icon: <FiLayers /> },
        { name: 'Product Category', path: '/ViewProductCategory', icon: <FiGlobe /> },
        { name: 'Product Barcodes', path: '/ViewProductBarcodes', icon: <FiGlobe /> },
        { name: 'UnitsOfMeasure', path: '/ViewUnitsOfMeasure', icon: <FiDollarSign /> },
        { name: 'ProductUomConversions', path: '/ViewProductUomConversions', icon: <FiGlobe /> },
        { name: 'Discounts', path: '/Discounts', icon: <FiDollarSign /> },
        { name: 'Taxes', path: '/Taxes', icon: <FiFileText /> },
      ]
    },
    {
      name: 'Daily Report',
      icon: <FiSettings />,
      path: null,
      submenu: [
        { name: 'Daily Summery', path: '/dailysummery', icon: <FiDollarSign /> },
      ]
    },
    {
      name: 'All Report',
      icon: <FiSettings />,
      path: null,
      submenu: [
        { name: 'Inventoryvaluation', path: '/inventorystock', icon: <FiDollarSign /> },
        { name: 'Stocksummery', path: '/stocksummery', icon: <FiDollarSign /> },
        { name: 'Lowstock', path: '/lowstockreport', icon: <FiDollarSign /> },
        { name: 'Purchase', path: '/purchasereport ', icon: <FiDollarSign /> },
        { name: 'Sales', path: '/salesreport ', icon: <FiDollarSign /> },
        { name: 'Customer', path: '/customerreport ', icon: <FiDollarSign /> },
        { name: 'Supplier', path: '/supplierreport ', icon: <FiDollarSign /> },
        { name: 'Customer Detail', path: '/customerdetailreport ', icon: <FiDollarSign /> },
        { name: 'Supplier Detail', path: '/supplierdetailreport ', icon: <FiDollarSign /> },
        { name: 'Expense Summery', path: '/expensesummery ', icon: <FiDollarSign /> },
        { name: 'Trial Balance', path: '/trialbalance ', icon: <FiDollarSign /> },
        { name: 'Balance Sheet', path: '/balancesheet ', icon: <FiDollarSign /> },
        { name: 'Income Statement', path: '/incomestatement ', icon: <FiDollarSign /> },
      ]
    },
  ];

  // ========== ADD A MENU FOR REGULAR USERS (role: 'user') ==========
  const userMenuItems = [
    { name: 'Dashboard', icon: <FiHome />, path: '/', submenu: [] },
    {
      name: 'Inventory',
      icon: <FiPackage />,
      path: null,
      submenu: [
        { name: 'Stock Adjustments', path: '/ViewStockAdjustments', icon: <FiTrendingUp /> },
      ]
    },
    {
      name: 'Sales',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Products', path: '/ViewProducts', icon: <FiPackage /> },
        { name: 'Customer', path: '/ViewCustomer', icon: <FiUsers /> },
        { name: 'Sales Order', path: '/ViewSalesOrder', icon: <FiFileText /> },
        { name: 'Sales', path: '/sales/invoices', icon: <FiFileText /> },
        { name: 'Customer Payment', path: '/ViewCustomerPayment', icon: <FiDollarSign /> },
      ]
    },
    
    {
      name: 'Purchase',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Inventory Transaction', path: '/ViewPurchaseInventoryTransaction', icon: <FiShoppingCart /> },
        { name: 'Suppliers', path: '/ViewSuppliers', icon: <FiUsers /> },
        { name: 'Purchase Order', path: '/ViewPurchaseOrder', icon: <FiFileText /> },
        { name: 'Purchase', path: '/purchase/invoices', icon: <FiFileText /> },
        { name: 'Supplier Payment', path: '/ViewSupplierPayment', icon: <FiDollarSign /> },
      ]
    },
    {
      name: 'Stock Transfer',
      icon: <FiTrendingUp />,
      path: null,
      submenu: [
        { name: 'Stock Transfer', path: '/ViewStockTransfer', icon: <FiTrendingUp /> },
      ]
    },
    {
      name: 'Expense Form',
      icon: <FiFileText />,
      path: '/ViewExpenses',
      submenu: []
    },
    {
      name: 'All Report',
      icon: <FiSettings />,
      path: null,
      submenu: [
        { name: 'Inventoryvaluation', path: '/inventorystock', icon: <FiDollarSign /> },
        { name: 'Stocksummery', path: '/stocksummery', icon: <FiDollarSign /> },
        { name: 'Lowstock', path: '/lowstockreport', icon: <FiDollarSign /> },
        { name: 'Purchase', path: '/purchasereport ', icon: <FiDollarSign /> },
        { name: 'Sales', path: '/salesreport ', icon: <FiDollarSign /> },
        { name: 'Customer', path: '/customerreport ', icon: <FiDollarSign /> },
        { name: 'Supplier', path: '/supplierreport ', icon: <FiDollarSign /> },
        { name: 'Customer Detail', path: '/customerdetailreport ', icon: <FiDollarSign /> },
        { name: 'Supplier Detail', path: '/supplierdetailreport ', icon: <FiDollarSign /> },
        { name: 'Expense Summery', path: '/expensesummery ', icon: <FiDollarSign /> },
        { name: 'Trial Balance', path: '/trialbalance ', icon: <FiDollarSign /> },
        { name: 'Balance Sheet', path: '/balancesheet ', icon: <FiDollarSign /> },
        { name: 'Income Statement', path: '/incomestatement ', icon: <FiDollarSign /> },
      ]
    },
    
  ];

  // Sales, Cashier, Purchase menus (unchanged)
  const salesMenuItems = [
    { name: 'Dashboard', icon: <FiHome />, path: '/', submenu: [] },
    {
      name: 'Inventory',
      icon: <FiPackage />,
      path: null,
      submenu: [
        { name: 'Stock Adjustments', path: '/ViewStockAdjustments', icon: <FiTrendingUp /> },
      ]
    },
    {
      name: 'Sales',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Products', path: '/ViewProducts', icon: <FiPackage /> },
        { name: 'Inventory Transaction', path: '/ViewSalesInventoryTransaction', icon: <FiShoppingCart /> },
        { name: 'Customer', path: '/ViewCustomer', icon: <FiUsers /> },
        { name: 'Sales Order', path: '/ViewSalesOrder', icon: <FiFileText /> },
        { name: 'Sales', path: '/sales/invoices', icon: <FiFileText /> },
        { name: 'Customer Payment', path: '/ViewCustomerPayment', icon: <FiDollarSign /> },
      ]
    }
  ];
  const cashierMenuItems = [
    { name: 'Dashboard', icon: <FiHome />, path: '/', submenu: [] },
    {
      name: 'Customer Payments',
      icon: <FiDollarSign />,
      path: '/ViewCustomerPayment',
      submenu: []
    },
    {
      name: 'Expense Form',
      icon: <FiFileText />,
      path: '/ViewExpenses',
      submenu: []
    },
    {
      name: 'Suppliers Payment',
      icon: <FiUsers />,
      path: '/ViewSupplierPayment',
      submenu: []
    }
  ];
  const purchaseMenuItems = [
    { name: 'Dashboard', icon: <FiHome />, path: '/', submenu: [] },
    {
      name: 'Inventory',
      icon: <FiPackage />,
      path: null,
      submenu: [
        { name: 'Stock Adjustments', path: '/ViewStockAdjustments', icon: <FiTrendingUp /> },
      ]
    },
    {
      name: 'Purchase',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Inventory Transaction', path: '/ViewPurchaseInventoryTransaction', icon: <FiShoppingCart /> },
        { name: 'Suppliers', path: '/ViewSuppliers', icon: <FiUsers /> },
        { name: 'Purchase Order', path: '/ViewPurchaseOrder', icon: <FiFileText /> },
        { name: 'Purchase', path: '/purchase/invoices', icon: <FiFileText /> },
        { name: 'Supplier Payment', path: '/ViewSupplierPayment', icon: <FiDollarSign /> },
      ]
    },
    {
      name: 'Stock Transfer',
      icon: <FiShoppingCart />,
      path: null,
      submenu: [
        { name: 'Stock Transfer', path: '/ViewStockTransfer', icon: <FiTrendingUp /> },
      ]
    }
  ];

  const getMenuItems = () => {  
    if (userType === 'system') {
      return systemMenuItems;
    }
    
    if (userType === 'company') {
      const role = currentUser?.role?.toLowerCase();
      switch (role) {
        case 'admin':
          return companyAdminMenuItems;
        case 'sales':
          return salesMenuItems;
        case 'cashier':
          return cashierMenuItems;
        case 'purchase':
          return purchaseMenuItems;
        case 'user':
          return userMenuItems;
        default:
          // Provide a fallback menu (e.g., only Dashboard) so user doesn't see empty sidebar
          return [{ name: 'Dashboard', icon: <FiHome />, path: '/', submenu: [] }];
      }
    }
    
    return [];
  };

  const menuItems = getMenuItems();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setSidebarOpen(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSubmenu = (name) => setActiveSubmenu(activeSubmenu === name ? null : name);
  const toggleSidebar = () => {
    if (isMobile) setSidebarOpen(!sidebarOpen);
    else setIsCollapsed(!isCollapsed);
  };
  const closeSidebarOnMobile = () => { if (isMobile) setSidebarOpen(false); };

  const handleNavigation = (item) => {
    if (item.action) {
      item.action(); // triggers backup
      return;
    }
    if (!item.path || item.path === '#') return;
    closeSidebarOnMobile();
    navigate(item.path);
  };

  return (
    <>
      <button className={`mobile-menu-button ${sidebarOpen ? 'hidden' : ''}`} onClick={toggleSidebar}>
        <FiMenu size={24} />
      </button>
      {sidebarOpen && isMobile && <div className="sidebar-overlay" onClick={closeSidebarOnMobile} />}
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${sidebarOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
  {(!isCollapsed || isMobile) && (
    <div className="logo-wrapper">
      <img src="/logo.png" alt="HantiZone" className="sidebar-logo" />
    </div>
  )}

  <button className="toggle-btn" onClick={toggleSidebar}>
    {isMobile ? <FiX /> : isCollapsed ? <FiMenu /> : <FiX />}
  </button>
</div>
<nav className="sidebar-nav">
  <ul>
    {menuItems.length === 0 ? (
      <li style={{ color: '#fff', padding: '15px', textAlign: 'center' }}>
        <div>No menu available for role: {currentUser?.role}</div>
      </li>
    ) : (
      menuItems.map((item) => (
        <li key={item.name}>
          {item.submenu && item.submenu.length > 0 ? (
            <>
              <div
                className="menu-item"
                onClick={() => toggleSubmenu(item.name)}
                style={{ cursor: 'pointer' }}
              >
                <span className="icon">{item.icon}</span>
                {(!isCollapsed || isMobile) && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{item.name}</span>
                    <span className="arrow">
                      {activeSubmenu === item.name ? <FiChevronDown /> : <FiChevronRight />}
                    </span>
                  </div>
                )}
              </div>
              {(!isCollapsed || isMobile) && (
                <ul className={`submenu ${activeSubmenu === item.name ? 'open' : ''}`}>
                  {item.submenu.map((sub) => (
                    <li key={sub.name}>
                      <div
                        onClick={() => handleNavigation(sub)}
                        style={{
                          cursor: 'pointer',
                          padding: '8px 16px 8px 45px',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#fff',
                        }}
                      >
                        {sub.icon && <span className="submenu-icon">{sub.icon}</span>}
                        <span>{sub.name}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div
              onClick={() => handleNavigation(item)}
              className="menu-item"
              style={{ cursor: 'pointer' }}
            >
              <span className="icon">{item.icon}</span>
              {(!isCollapsed || isMobile) && <span>{item.name}</span>}
            </div>
          )}
        </li>
      ))
    )}
  </ul>
</nav>
      </div>
    </>
  );
};

export default Sidebar;
